import { useIsAdmin } from '../../../api/session';
import type { TicketDetail } from '../../../api/ticket-types';
import { StatusBadge } from '../../../ui/Badge';
import { ActorName, Card, KeyValue } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';
import { ExternalReporterLabel, PriorityBadge } from '../bits';
import { CodeCard } from '../CodeCard';
import { Triage } from './Editing';

export function Side({ t }: { t: TicketDetail }) {
  const admin = useIsAdmin();
  return (
    <div className="stack">
      <Card title="Details" tight>
        <KeyValue
          items={[
            ['Status', <StatusBadge key="s" status={t.status} resolution={t.resolution} />],
            ['Priority', <PriorityBadge key="p" priority={t.priority} />],
            ['Reporter', <ActorName key="r" actor={t.reporter} />],
            ...(t.externalReporter
              ? ([
                  ['Reported for', <ExternalReporterLabel key="x" reporter={t.externalReporter} withEmail />],
                ] as [string, React.ReactNode][])
              : []),
            [
              'Assignee',
              t.assignee ? (
                <ActorName actor={t.assignee} />
              ) : (
                <span className="muted">
                  {t.status === 'todo' ? 'Unassigned · AI-claimable' : 'Unassigned'}
                </span>
              ),
            ],
            [
              'Owner',
              t.owner ? (
                <span>
                  {t.owner.name}
                  {t.owner.kind === 'ai_service' ? ' (AI)' : ''}
                  {t.owner.state === 'stale' ? ' · stale' : ''}
                </span>
              ) : (
                '—'
              ),
            ],
            ['Labels', t.labels.length ? t.labels.join(', ') : '—'],
            ['Created', <Timestamp key="c" value={t.createdAt} />],
            ['Updated', <Timestamp key="u" value={t.updatedAt} />],
            ['In column since', <Timestamp key="sc" value={t.statusChangedAt} />],
            ...(t.closedAt
              ? ([['Closed', <Timestamp key="cl" value={t.closedAt} />]] as [string, React.ReactNode][])
              : []),
          ]}
        />
      </Card>
      <CodeCard t={t} />
      {admin && !t.project.archived && t.status !== 'closed' && <Triage t={t} />}
      <Card title="Watchers" tight>
        <span className="small">
          {t.watchers
            .filter(Boolean)
            .map((w) => w!.name)
            .join(', ') || 'Nobody'}
        </span>
      </Card>
    </div>
  );
}
